import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import BootstrapTable, { SelectRowProps } from "react-bootstrap-table-next";
import useAuth from "../../login/AuthContext";
import Api from "../../../services/Api";
import paginationFactory from "react-bootstrap-table2-paginator";
import { AdminEditModal } from "./AdminEditModal";
import Typography from "@mui/material/Typography";
import { AdminInfo, getAdminUsers } from "../../../services/AdminApi";

export const AdminNetworkAccessControls = () => {
  const { getAuthToken } = useAuth();

  const [nameFilter, setNameFilter] = useState<string | null>(null);
  const [reload, setReload] = useState(false);
  const [adminResults, setAdminResults] = useState<{
    users: AdminInfo[];
    totalUsers: number;
    page: number;
    size: number;
  }>({
    users: [],
    totalUsers: 0,
    page: 0,
    size: 10,
  });
  const [adminEditModal, setAdminEditModal] = useState<{ show: boolean; currentUser: AdminInfo | null }>({
    show: false,
    currentUser: null,
  });

  useEffect(() => {
    const handler = setTimeout(async () => {
      const adminUsersResponse = await getAdminUsers(nameFilter, adminResults.page, await getAuthToken());

      if (adminUsersResponse) {
        setAdminResults({
          users: adminUsersResponse.content,
          totalUsers: adminUsersResponse.totalElements,
          page: adminUsersResponse.number,
          size: adminUsersResponse.size,
        });
      }

      setReload(false);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [nameFilter, adminResults.page, reload]);

  async function saveChanges(adminId: number, changes: any) {
    const update = await Api.updateAdminUser(adminId, changes, await getAuthToken());

    if (update.success) {
      setReload(true);
      setAdminEditModal({ show: false, currentUser: null });
    }
  }

  const changePage = (page: number) => {
    const results = { ...adminResults, page: page };
    setAdminResults(results);
  };

  return (
    <div>
      <AdminEditModal
        show={adminEditModal.show}
        adminUser={adminEditModal.currentUser}
        rejectChange={() => {
          setAdminEditModal({ show: false, currentUser: null });
        }}
        saveChanges={saveChanges}
      />
      <Typography gutterBottom variant="h5">
        Network Administrators
      </Typography>
      <AdminSearch setNameFilter={setNameFilter} />

      <AdminUsersTable
        adminUsers={adminResults.users}
        totalAdminUsers={adminResults.totalUsers}
        page={adminResults.page}
        pageSize={adminResults.size}
        selectUserToEdit={(user) => {
          setAdminEditModal({ show: true, currentUser: user });
        }}
        changePage={changePage}
      />
    </div>
  );
};

const AdminSearch: React.FunctionComponent<{ setNameFilter: React.Dispatch<React.SetStateAction<string | null>> }> = ({
  setNameFilter,
}) => (
  <Form>
    <Row>
      <Col>
        <Form.Label>Filter by Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Filter by Name"
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setNameFilter(e.target.value);
          }}
        />
      </Col>
      <Col />
      <Col />
    </Row>
  </Form>
);

interface AdminUsersTableProps {
  adminUsers: AdminInfo[];
  totalAdminUsers: number;
  pageSize: number;
  page: number;
  changePage: (page: number) => void;
  selectUserToEdit: (user: AdminInfo) => void;
}

const AdminUsersTable: React.FunctionComponent<AdminUsersTableProps> = ({
  adminUsers,
  totalAdminUsers,
  pageSize,
  page,
  changePage,
  selectUserToEdit,
}) => {
  const selectRow: SelectRowProps<AdminInfo> = {
    mode: "radio",
    onSelect: (row: AdminInfo, isSelect: boolean, rowIndex: number, e: any) => {
      selectUserToEdit(row);
    },
    selectionRenderer: ({ mode, ...rest }) => (
      <Button type={mode} className={"btn"}>
        Edit
      </Button>
    ),
  };

  const paginationOptions = {
    totalSize: totalAdminUsers,
    sizePerPage: pageSize,
    showTotal: true,
    hideSizePerPage: true,
    page: page + 1,
  };

  const columns = [
    {
      dataField: "name",
      text: "Name",
    },
    {
      dataField: "email",
      text: "Email",
    },
    {
      dataField: "networkNames",
      text: "Networks",
      formatter: (cell: any, row: AdminInfo) => {
        let networkNames: string[] = [];
        if (row.networks) {
          networkNames = row.networks.reduce((acc, curr) => {
            acc.push(curr.name);
            return acc;
          }, networkNames);
        }

        return networkNames.join(", ");
      },
    },
    {
      dataField: "identities",
      text: "Identities",
    },
    {
      dataField: "phone",
      text: "Phone Number",
    },
  ];

  const onTableChange = (type: any, { page }: any) => {
    changePage(page - 1);
  };

  return (
    <BootstrapTable
      keyField="adminId"
      remote
      pagination={paginationFactory(paginationOptions)}
      data={adminUsers || []}
      columns={columns}
      striped
      hover
      selectRow={selectRow}
      onTableChange={onTableChange}
      noDataIndication={"No results found"}
    />
  );
};
